/**
 * Catalog filter model. The same parser runs in the browser (URL <-> UI state)
 * and in the API (query string -> SQL), so both sides always agree.
 */

export const SORT_OPTIONS = ["featured", "newest", "price_asc", "price_desc", "name_asc"] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE = 12;
export const MAX_PAGE_SIZE = 48;
export const MAX_QUERY_LENGTH = 80;

export interface CatalogFilters {
  q: string;
  games: string[];
  categories: string[];
  /** Card set (expansion) names, e.g. "SV08: Surging Sparks" (repeated ?set=). */
  sets: string[];
  /** Card rarities as stored in the catalog, e.g. "Double Rare". */
  rarities: string[];
  /** Whole US dollars (prices are USD). */
  minPrice: number | null;
  maxPrice: number | null;
  inStock: boolean;
  /** Pre-sale products only (?preventa=1). */
  presale: boolean;
  sort: SortOption;
  page: number;
  pageSize: number;
}

export const DEFAULT_FILTERS: Readonly<CatalogFilters> = Object.freeze({
  q: "",
  games: [],
  categories: [],
  sets: [],
  rarities: [],
  minPrice: null,
  maxPrice: null,
  // Shoppers see what they can buy: in-stock only unless they untick it (?stock=0).
  inStock: true,
  presale: false,
  sort: "featured",
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
});

const SLUG_RE = /^[a-z0-9-]{1,40}$/;

function parseList(value: string | null): string[] {
  if (!value) return [];
  const unique = new Set(
    value
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter((s) => SLUG_RE.test(s)),
  );
  return [...unique].slice(0, 20);
}

/** Free-text values (set names, rarities): trimmed, bounded, repeated params (?rarity=a&rarity=b). */
function parseTexts(values: string[], maxItems: number, maxLength: number): string[] {
  const clean = values.map((v) => v.trim().replace(/\s+/g, " ")).filter((v) => v.length > 0 && v.length <= maxLength);
  return [...new Set(clean)].slice(0, maxItems);
}

function parsePrice(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

function parseIntInRange(value: string | null, fallback: number, min: number, max: number): number {
  const n = Number(value);
  if (!Number.isInteger(n) || n < min) return fallback;
  return Math.min(n, max);
}

/** Parses untrusted query params into a normalized, bounded filter object. */
export function parseFilters(params: URLSearchParams): CatalogFilters {
  const sortParam = params.get("sort");
  const sort = (SORT_OPTIONS as readonly string[]).includes(sortParam ?? "")
    ? (sortParam as SortOption)
    : DEFAULT_FILTERS.sort;

  let minPrice = parsePrice(params.get("min"));
  let maxPrice = parsePrice(params.get("max"));
  if (minPrice !== null && maxPrice !== null && minPrice > maxPrice) {
    [minPrice, maxPrice] = [maxPrice, minPrice];
  }

  return {
    q: (params.get("q") ?? "").trim().replace(/\s+/g, " ").slice(0, MAX_QUERY_LENGTH),
    games: parseList(params.get("game")),
    categories: parseList(params.get("category")),
    sets: parseTexts(params.getAll("set"), 10, 120),
    rarities: parseTexts(params.getAll("rarity"), 10, 60),
    minPrice,
    maxPrice,
    inStock: params.get("stock") !== "0",
    presale: params.get("preventa") === "1",
    sort,
    page: parseIntInRange(params.get("page"), 1, 1, 10_000),
    pageSize: parseIntInRange(params.get("pageSize"), DEFAULT_PAGE_SIZE, 1, MAX_PAGE_SIZE),
  };
}

/** Serializes filters back to query params, omitting defaults for clean URLs. */
export function serializeFilters(filters: CatalogFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.games.length) params.set("game", filters.games.join(","));
  if (filters.categories.length) params.set("category", filters.categories.join(","));
  for (const s of filters.sets) params.append("set", s);
  for (const r of filters.rarities) params.append("rarity", r);
  if (filters.minPrice !== null) params.set("min", String(filters.minPrice));
  if (filters.maxPrice !== null) params.set("max", String(filters.maxPrice));
  if (!filters.inStock) params.set("stock", "0");
  if (filters.presale) params.set("preventa", "1");
  if (filters.sort !== DEFAULT_FILTERS.sort) params.set("sort", filters.sort);
  if (filters.page !== 1) params.set("page", String(filters.page));
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(filters.pageSize));
  return params;
}

/** Returns a new filter object with `patch` applied; any change other than paging resets to page 1. */
export function updateFilters(filters: CatalogFilters, patch: Partial<CatalogFilters>): CatalogFilters {
  return { ...filters, ...patch, page: patch.page ?? 1 };
}

/** Adds the value if absent, removes it if present. */
export function toggleValue(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function countActiveFilters(filters: CatalogFilters): number {
  return (
    filters.games.length +
    filters.categories.length +
    filters.sets.length +
    filters.rarities.length +
    (filters.minPrice !== null ? 1 : 0) +
    (filters.maxPrice !== null ? 1 : 0) +
    (filters.inStock !== DEFAULT_FILTERS.inStock ? 1 : 0) +
    (filters.presale ? 1 : 0) +
    (filters.q ? 1 : 0)
  );
}
